import { NEUTRAL_ANSWER_WEIGHT } from '../components/shared/questionEntry/config';
import {
  isArray,
  isArrayEmpty,
  isObjectEmpty,
  calcLargestRemainderPercentage,
} from './helpers';
import { getPersonFullName, preparenpsEnpsAttributes } from './uiUtils';
import { prepareAnswerWeight } from './attribute';
import { SURVEY_TYPES, SURVEY_TYPE_FILTER_OPTIONS } from '../constants/survey';
import { ATTRIBUTE_TYPES_MAP } from '../components/pages/attributesPage/config';
import { BAR_CHART_COLORS } from '../components/shared/npsReviewReport/reportChart/config';

export const prepareSurveyTypeFilterOptions = (
  labels,
  shouldIncludePulse = false
) =>
  SURVEY_TYPE_FILTER_OPTIONS.reduce((acc, option, index, array) => {
    if (!shouldIncludePulse && index === array.length - 1) {
      return acc;
    }

    return [
      ...acc,
      {
        ...option,
        label: labels[option.translationKey],
      },
    ];
  }, []);

export const transformSurveyData = survey => ({
  ...survey,
  attributes: preparenpsEnpsAttributes(survey.type, survey.attributes),
  createdAt: survey.created_at,
  sentAt: survey.sent_at,
  createdFor: survey?.created_for?.map(user => ({
    ...user,
    name: getPersonFullName(user),
  })),
  isAnonymous: survey.is_anonymous,
  isOneTime: !survey.is_statistical,
  hasForceFinish: survey.force_finish_available,
  reviewers: survey?.reviewers?.map(user => ({
    ...user,
    name: getPersonFullName(user),
  })),
  ...(survey?._reviewers
    ? {
        rawReviewers: survey?._reviewers?.map(user => ({
          ...user,
          name: getPersonFullName(user),
        })),
      }
    : {}),
  scheduledFor: survey.scheduled_for,
  autoSharedUsers: survey?.pre_shared?.pre_shared_users,
  autoSharedReport: survey?.pre_shared?.pre_shared_report,
  expireAt: survey.expire_at,
  autoReminders: survey.auto_reminders || [],
});

export const transformReviewsData = reviews =>
  reviews.map(review => ({
    ...review,
    averageScore: +review.average_score || null,
    finishedCount: review.number_of_finished,
    isTextReply: review.text_reply_questions,
  }));

export const prepareReviewParticipants = (surveyType, participants = {}) => {
  const isTeamReview = surveyType === SURVEY_TYPES.TEAM;

  if (isObjectEmpty(participants)) return [];

  return isTeamReview ? participants.subjects : participants.reviewers;
};

export const transformReviewData = (review, surveyType = null) => ({
  ...review,
  isEnps: review.type === ATTRIBUTE_TYPES_MAP.ENPS.id,
  hasAdditionalFeedback: review.feedback_field,
  hasNeutralAnswer: review.with_neutral_answer,
  isTextReply: review.text_reply_questions,
  totalAnswers: review.with_neutral_answer
    ? review.answer_count + 1
    : review.answer_count,
  ...(surveyType
    ? {
        participants: prepareReviewParticipants(surveyType, review),
        answers: review.text_reply_questions ? review.replies : review.answers,
      }
    : {}),
  ...(review.average_score
    ? { averageScore: +review.average_score || null }
    : {}),
});

export const getReviewGridColumns = (
  labels,
  totalAnswers,
  hasNeutralAnswer
) => {
  return Array.from({ length: totalAnswers + 1 }, (_, i) => {
    if (i === 0) {
      return {
        name: labels.question,
      };
    }

    if (hasNeutralAnswer && i === totalAnswers) {
      return {
        name: labels.neutral.shorthand,
      };
    }

    return {
      name: `${labels.answerShorthand}${i}`,
    };
  });
};

export const prepareAnswerGridResults = (results, selectedUsers) => {
  if (isObjectEmpty(results) || isArrayEmpty(selectedUsers)) return results;

  return Object.keys(results).reduce((acc, questionKey) => {
    const answers = Object.keys(results[questionKey]).reduce(
      (answersAcc, answerKey) => {
        const answerResult = results[questionKey][answerKey].filter(user =>
          selectedUsers.includes(user.id)
        );

        if (!isArrayEmpty(answerResult)) {
          return { ...answersAcc, [answerKey]: answerResult };
        }

        return answersAcc;
      },
      {}
    );

    if (!isObjectEmpty(answers)) {
      return { ...acc, [questionKey]: answers };
    }

    return acc;
  }, {});
};

export const prepareTextReplyResults = (
  results,
  selectedUsers,
  shouldFilterResults
) => {
  if (
    isObjectEmpty(results) ||
    isArrayEmpty(selectedUsers) ||
    !shouldFilterResults
  )
    return results;

  return Object.keys(results).reduce((acc, questionKey) => {
    const questionResults = results[questionKey].filter(result =>
      selectedUsers.includes(result.user.id)
    );

    if (!isArrayEmpty(questionResults)) {
      return { ...acc, [questionKey]: questionResults };
    }

    return acc;
  }, {});
};

const calculateAverage = (part, total) => {
  if (part && total) {
    return Math.round((part / total + Number.EPSILON) * 10) / 10;
  }
  return 0;
};

const prepareNPSMetrics = (responses, reviewersCount) => {
  const detractorsCount = responses
    .slice(0, 6)
    .reduce((acc, currentCount) => acc + currentCount, 0);
  const neutralsCount = responses
    .slice(6, 8)
    .reduce((acc, currentCount) => acc + currentCount, 0);
  const promotersCount = responses
    .slice(8, 10)
    .reduce((acc, currentCount) => acc + currentCount, 0);
  const [detractorsPercentage, neutralsPercentage, promotersPercentage] =
    calcLargestRemainderPercentage([
      detractorsCount / reviewersCount,
      neutralsCount / reviewersCount,
      promotersCount / reviewersCount,
    ]);

  const score = Math.round(promotersPercentage - detractorsPercentage);

  return {
    score,
    promoters: {
      percentage: promotersPercentage,
      count: promotersCount,
    },
    neutrals: {
      percentage: neutralsPercentage,
      count: neutralsCount,
    },
    detractors: {
      percentage: detractorsPercentage,
      count: detractorsCount,
    },
  };
};

export const getEnpsResults = (
  answers = {},
  replies = {},
  completedCount = 0
) => {
  if (!answers || isObjectEmpty(answers))
    return {
      chartData: [],
      replies: [],
      responses: [],
    };

  const results = Array.from({ length: 10 }, (_, i) => 1 + i).reduce(
    (acc, currentValue) => {
      const userResponse = answers[currentValue];
      const userResponsePercentages = userResponse
        ? userResponse / completedCount
        : 0;
      const answerReplies = replies?.[currentValue]?.reduce(
        (commentAcc, currentComment) => {
          return [
            ...commentAcc,
            {
              text: currentComment.reply,
              color: BAR_CHART_COLORS[currentValue],
              user: currentComment.user,
            },
          ];
        },
        []
      );

      return {
        ...acc,
        chartData: [
          ...acc.chartData,
          {
            x: currentValue,
            count: userResponse || 0,
          },
        ],
        repliesPercentages: [
          ...acc.repliesPercentages,
          userResponsePercentages,
        ],
        responses: [...acc.responses, userResponse || 0],
        replies: [...acc.replies, ...(answerReplies || [])],
      };
    },
    {
      chartData: [],
      replies: [],
      repliesPercentages: [],
      responses: [],
    }
  );

  const metricsNPS = prepareNPSMetrics(results.responses, completedCount);
  const formatedRepliesPercentages = calcLargestRemainderPercentage(
    results.repliesPercentages
  );
  const chartData = results.chartData.map((data, index) => ({
    ...data,
    y: formatedRepliesPercentages[index] || null,
  }));

  return {
    ...results,
    chartData,
    metrics: metricsNPS,
  };
};

export const getReportChartResults = (
  results,
  questions,
  hasNeutralAnswer,
  selectedUsers = [],
  isAverageView = false
) => {
  if (
    isObjectEmpty(results) ||
    isArrayEmpty(questions) ||
    isArrayEmpty(selectedUsers)
  )
    return [];

  if (isAverageView) {
    const weightArray = selectedUsers.reduce((userResults, userId) => {
      const userResult = questions.reduce((acc, question, questionIndex) => {
        const selectedAnswerId = Object.keys(results[question.id]).find(
          answerId => {
            return results[question.id][answerId].some(
              userAnswer => userAnswer.id === userId
            );
          }
        );
        const answerIndex = question.answers.findIndex(
          answer => answer.id === +selectedAnswerId
        );
        const answerWeight =
          prepareAnswerWeight(question.answers[answerIndex]?.weight) || 0;
        const weightList = userResults[questionIndex]
          ? userResults[questionIndex].concat(answerWeight)
          : [answerWeight];

        return {
          ...acc,
          [questionIndex]:
            answerWeight === NEUTRAL_ANSWER_WEIGHT
              ? userResults[questionIndex]
              : weightList,
        };
      }, {});

      return { ...userResult };
    }, {});

    return [
      Object.values(weightArray).map(weights => {
        if (!weights) {
          return null;
        }

        const answerCount = weights?.length;
        const totalWeights = weights.reduce((acc, curr) => acc + curr, 0);

        return calculateAverage(totalWeights, answerCount);
      }),
    ];
  }

  return selectedUsers.reduce((userResults, userId) => {
    const userResult = questions.reduce((acc, question) => {
      const selectedAnswerId = Object.keys(results[question.id]).find(
        answerId => {
          return results[question.id][answerId].some(
            userAnswer => userAnswer.id === userId
          );
        }
      );
      const answerIndex = question.answers.findIndex(
        answer => answer.id === +selectedAnswerId
      );
      const isNeutralAnswer =
        hasNeutralAnswer && answerIndex === question.answers.length - 1;

      return [...acc, isNeutralAnswer ? 0 : answerIndex + 1];
    }, []);

    return [...userResults, userResult];
  }, []);
};

export const getToggleReportViewItems = (labels, isChartView = false) => [
  {
    value: false,
    label: isChartView ? labels.answers : labels.people,
  },
  {
    value: true,
    label: isChartView ? labels.score : labels.percentage,
  },
];

export const getResponseCountPerQuestion = questionResults => {
  return Object.keys(questionResults).reduce((totalScore, answerKey) => {
    const currentScore =
      questionResults[answerKey]?.length || questionResults[answerKey];
    return totalScore + currentScore;
  }, 0);
};

export const getQuestionAverage = (row, result, totalResponses) => {
  let totalResponsesTemp = totalResponses;
  const total = row.answers.reduce(
    (acc, curr) => {
      let answerCount = result[curr.id]?.length || result[curr.id] || 0;

      if (
        answerCount &&
        prepareAnswerWeight(curr.weight) === NEUTRAL_ANSWER_WEIGHT
      ) {
        const neutralAnswer = result[curr.id];
        const neutralAnswerCount = isArray(neutralAnswer)
          ? neutralAnswer?.length
          : neutralAnswer;

        answerCount = 0;
        totalResponsesTemp -= neutralAnswerCount;
      }
      return {
        ...acc,
        totalAnsweredWeight:
          acc.totalAnsweredWeight +
          answerCount * prepareAnswerWeight(curr.weight),
      };
    },
    { totalAnsweredWeight: 0 }
  );
  const { totalAnsweredWeight } = total;

  return calculateAverage(totalAnsweredWeight, totalResponsesTemp);
};

export const transformQuestionnairesData = questionnaires =>
  questionnaires.map(questionnaire => ({
    ...questionnaire,
    finishedCount: questionnaire.number_of_finished,
  }));

export const normalizeQuestionnaireTemplateQuestions = (questions = []) => {
  if (!isArray(questions) || isArrayEmpty(questions)) {
    return [];
  }

  return [...questions]
    .sort((a, b) => (a.order || a.id) - (b.order || b.id))
    .map(question => ({
      ...question,
      options: question.options || [],
    }));
};

const emptyQuestionnaireAnswers = () => ({
  selectAnswers: {},
  textAnswers: {},
  rankingAnswers: {},
});

const appendUserToOptionResults = (results, questionId, optionId, user) => {
  const questionResults = results[questionId] || {};
  const optionResults = questionResults[optionId] || [];
  const isDuplicate = optionResults.some(
    existingUser => existingUser.id === user.id
  );

  if (isDuplicate) {
    return results;
  }

  return {
    ...results,
    [questionId]: {
      ...questionResults,
      [optionId]: [...optionResults, user],
    },
  };
};

const applyQuestionnaireResponseRecord = (acc, response) => {
  const questionId = Number(response.question);
  const user = response.user ?? response.reviewer ?? response.subject;

  if (!Number.isFinite(questionId) || !user) {
    return acc;
  }

  const selectedOptionIds = response.selected_option_ids;

  if (isArray(selectedOptionIds) && !isArrayEmpty(selectedOptionIds)) {
    const selectAnswers = selectedOptionIds.reduce(
      (selectAcc, optionId) =>
        appendUserToOptionResults(selectAcc, questionId, optionId, user),
      acc.selectAnswers
    );

    return { ...acc, selectAnswers };
  }

  const textValue = response.text_value;

  if (textValue) {
    const questionResults = acc.textAnswers[questionId] || [];

    return {
      ...acc,
      textAnswers: {
        ...acc.textAnswers,
        [questionId]: [...questionResults, { user, reply: textValue }],
      },
    };
  }

  const rankedOptionIds = response.ranked_option_ids;

  if (isArray(rankedOptionIds) && !isArrayEmpty(rankedOptionIds)) {
    const questionResults = acc.rankingAnswers[questionId] || [];

    return {
      ...acc,
      rankingAnswers: {
        ...acc.rankingAnswers,
        [questionId]: [...questionResults, { user, rankedOptionIds }],
      },
    };
  }

  return acc;
};

const transformQuestionnaireResponseMap = responses =>
  Object.keys(responses).reduce((acc, questionId) => {
    const questionData = responses[questionId];

    if (isArray(questionData)) {
      return questionData.reduce(
        (innerAcc, record) =>
          applyQuestionnaireResponseRecord(innerAcc, {
            ...record,
            question: record.question ?? questionId,
          }),
        acc
      );
    }

    return {
      ...acc,
      selectAnswers: {
        ...acc.selectAnswers,
        [questionId]: questionData,
      },
    };
  }, emptyQuestionnaireAnswers());

export const transformQuestionnaireResponses = responses => {
  if (isObjectEmpty(responses)) {
    return emptyQuestionnaireAnswers();
  }

  if (isArray(responses)) {
    return responses.reduce(
      applyQuestionnaireResponseRecord,
      emptyQuestionnaireAnswers()
    );
  }

  return transformQuestionnaireResponseMap(responses);
};

const hasPreparedQuestionnaireAnswers = questionnaire =>
  !!(
    questionnaire.selectAnswers ||
    questionnaire.textAnswers ||
    questionnaire.rankingAnswers
  );

export const transformQuestionnaireSummaryData = (
  questionnaire,
  surveyType
) => {
  const questionnaireAnswers = hasPreparedQuestionnaireAnswers(questionnaire)
    ? {
        selectAnswers: questionnaire.selectAnswers || {},
        textAnswers: questionnaire.textAnswers || {},
        rankingAnswers: questionnaire.rankingAnswers || {},
      }
    : transformQuestionnaireResponses(
        questionnaire.answers || questionnaire.responses || {}
      );

  return {
    ...questionnaire,
    finishedCount:
      questionnaire.finishedCount ?? questionnaire.number_of_finished ?? 0,
    questions: normalizeQuestionnaireTemplateQuestions(questionnaire.questions),
    participants: prepareReviewParticipants(surveyType, questionnaire),
    selectAnswers: questionnaireAnswers.selectAnswers,
    textAnswers: questionnaireAnswers.textAnswers,
    rankingAnswers: questionnaireAnswers.rankingAnswers,
  };
};

export const prepareQuestionnaireRankingResults = (
  results,
  selectedUsers,
  shouldFilterResults
) => {
  if (
    isObjectEmpty(results) ||
    isArrayEmpty(selectedUsers) ||
    !shouldFilterResults
  ) {
    return results;
  }

  return Object.keys(results).reduce((acc, questionKey) => {
    const questionResults = results[questionKey].filter(result =>
      selectedUsers.includes(result.user.id)
    );

    if (!isArrayEmpty(questionResults)) {
      return { ...acc, [questionKey]: questionResults };
    }

    return acc;
  }, {});
};
